
import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, Edit, Trash2, Archive, ArchiveRestore } from "lucide-react";
import DeleteArticleDialog from "./DeleteArticleDialog";
import { getStatusBadge } from "./Badges";
import type { Article } from "@/types/article";

type Props = {
  articles: Article[];
  loading: boolean;
  filteredArticles: Article[];
  deleteDialog: { open: boolean; article: Article | null };
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onView: (id: number | string) => void;
  onEdit: (id: number | string) => void;
  onOpenDelete: (article: Article) => void;
  onCloseDelete: () => void;
  onDelete: (id: number | string) => void;
  onArchive: (id: number | string) => void;
  onUnarchive?: (id: number | string) => void; // Only for archived table
  searchTerm: string;
  archiveMode?: boolean;
};

const ArticleManagementTable: React.FC<Props> = ({
  filteredArticles,
  loading,
  onView,
  onEdit,
  onOpenDelete,
  deleteDialog,
  onCloseDelete,
  onDelete,
  onArchive,
  onUnarchive,
  searchTerm,
  archiveMode = false,
}) => (
  <div>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Author</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Premium</TableHead>
          <TableHead>Views</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center">Loading articles…</TableCell>
          </TableRow>
        ) : filteredArticles.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center text-gray-600">No articles found.</TableCell>
          </TableRow>
        ) : filteredArticles.map((article) => (
          <TableRow key={article.id}>
            <TableCell className="font-medium">{article.title}</TableCell>
            <TableCell>{article.category}</TableCell>
            <TableCell>{article.author}</TableCell>
            <TableCell>{getStatusBadge(article.status)}</TableCell>
            <TableCell>{article.premium ? "Yes" : "No"}</TableCell>
            <TableCell>{article.views}</TableCell>
            <TableCell>
              <div className="flex space-x-2">
                <Button variant="ghost" size="sm" onClick={() => onView(article.id)}>
                  <Eye className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onEdit(article.id)}>
                  <Edit className="h-4 w-4" />
                </Button>
                {!archiveMode ? (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => onArchive(article.id)}>
                      <Archive className="h-4 w-4 text-yellow-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onOpenDelete(article)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => onUnarchive?.(article.id)}>
                    <ArchiveRestore className="h-4 w-4 text-green-600" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <DeleteArticleDialog
      open={deleteDialog.open}
      article={deleteDialog.article}
      onClose={onCloseDelete}
      onDelete={onDelete}
    />
  </div>
);

export default ArticleManagementTable;
